import { clearCookiesOnLogout } from '../store/utils/api';
import Auth from '../api/auth';

/**
 * SessionManager - Manages single session across browser tabs
 * Uses BroadcastChannel API to communicate between tabs
 * When a new login is detected in another tab, closes current session
 */
class SessionManager {
  constructor() {
    this.channel = null;
    this.channelName = 'chatwoot-session-channel';
    this.isInitialized = false;
    this.tabId = this.generateTabId();
    this.lastActiveTimestamp = Date.now();
  }

  /**
   * Generate unique ID for this tab
   */
  generateTabId() {
    return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize BroadcastChannel and set up listeners
   * Should be called when the app loads
   */
  initialize() {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel === 'undefined') {
      // Fallback: Use localStorage events for older browsers
      this.initializeLocalStorageFallback();
      return;
    }

    try {
      this.channel = new BroadcastChannel(this.channelName);
      this.setupChannelListener();
      this.isInitialized = true;
      
      // If user is already logged in, notify other tabs that this tab is active
      // This will close other tabs that were opened earlier
      if (Auth.hasAuthCookie()) {
        this.notifyTabActive();
      }
    } catch (error) {
      console.error('Failed to initialize BroadcastChannel:', error);
      // Fallback to localStorage
      this.initializeLocalStorageFallback();
    }
  }

  /**
   * Set up listener for BroadcastChannel messages
   */
  setupChannelListener() {
    if (!this.channel) return;

    this.channel.onmessage = event => {
      const { type, userId, timestamp, tabId } = event.data;

      if (type === 'NEW_LOGIN') {
        this.handleNewLogin(userId, timestamp);
      } else if (type === 'TAB_ACTIVE') {
        this.handleTabActive(tabId, userId, timestamp);
      }
    };
  }

  /**
   * Fallback for browsers that don't support BroadcastChannel
   * Uses localStorage events instead
   */
  initializeLocalStorageFallback() {
    window.addEventListener('storage', event => {
      if (event.key === 'chatwoot-new-login' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          this.handleNewLogin(data.userId, data.timestamp);
        } catch (error) {
          console.error('Error parsing localStorage login event:', error);
        }
      } else if (event.key === 'chatwoot-tab-active' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          this.handleTabActive(data.tabId, data.userId, data.timestamp);
        } catch (error) {
          console.error('Error parsing localStorage tab active event:', error);
        }
      }
    });
    
    // If user is already logged in, notify other tabs
    if (Auth.hasAuthCookie()) {
      this.notifyTabActive();
    }
  }

  /**
   * Handle new login detected from another tab
   * Closes current session if user is logged in
   */
  handleNewLogin(newUserId, timestamp) {
    // Only act if user is currently logged in
    if (!Auth.hasAuthCookie()) {
      return;
    }

    // Get current user ID from auth data
    const authData = Auth.getAuthData();
    if (!authData || !authData.uid) {
      return;
    }

    const currentUserId = authData.uid;

    // Only close session if it's the same user
    // (different user logins shouldn't affect current session)
    if (currentUserId === newUserId) {
      // Small delay to ensure the new session is fully established
      setTimeout(() => {
        clearCookiesOnLogout();
      }, 100);
    }
  }

  /**
   * Handle tab active notification from another tab
   * If another tab is more recent, close this tab's session
   */
  handleTabActive(otherTabId, userId, timestamp) {
    // Ignore if it's this same tab
    if (otherTabId === this.tabId) {
      return;
    }

    // Only act if user is currently logged in
    if (!Auth.hasAuthCookie()) {
      return;
    }

    // Get current user ID from auth data
    const authData = Auth.getAuthData();
    if (!authData || !authData.uid) {
      return;
    }

    const currentUserId = authData.uid;

    // Only close session if it's the same user and the other tab is more recent
    if (currentUserId === userId && timestamp > this.lastActiveTimestamp) {
      // Another tab is more recent, close this session
      clearCookiesOnLogout();
    }
  }

  /**
   * Notify other tabs that a new login has occurred
   * Should be called after successful login
   * @param {string} userId - The user ID that just logged in
   */
  notifyNewLogin(userId) {
    const timestamp = Date.now();
    this.lastActiveTimestamp = timestamp;
    
    const message = {
      type: 'NEW_LOGIN',
      userId,
      timestamp,
      tabId: this.tabId,
    };

    // Send via BroadcastChannel if available
    if (this.channel) {
      try {
        this.channel.postMessage(message);
      } catch (error) {
        console.error('Error sending BroadcastChannel message:', error);
      }
    }

    // Also set localStorage for fallback (will trigger storage event in other tabs)
    try {
      localStorage.setItem('chatwoot-new-login', JSON.stringify(message));
      // Remove immediately to allow future events
      setTimeout(() => {
        localStorage.removeItem('chatwoot-new-login');
      }, 100);
    } catch (error) {
      console.error('Error setting localStorage login event:', error);
    }
  }

  /**
   * Notify other tabs that this tab is active
   * Should be called when tab loads with active session
   */
  notifyTabActive() {
    if (!Auth.hasAuthCookie()) {
      return;
    }

    const authData = Auth.getAuthData();
    if (!authData || !authData.uid) {
      return;
    }

    const userId = authData.uid;
    const timestamp = Date.now();
    this.lastActiveTimestamp = timestamp;

    const message = {
      type: 'TAB_ACTIVE',
      userId,
      timestamp,
      tabId: this.tabId,
    };

    // Send via BroadcastChannel if available
    if (this.channel) {
      try {
        this.channel.postMessage(message);
      } catch (error) {
        console.error('Error sending BroadcastChannel tab active message:', error);
      }
    }

    // Also set localStorage for fallback
    try {
      localStorage.setItem('chatwoot-tab-active', JSON.stringify(message));
      // Remove after short delay to allow future events
      setTimeout(() => {
        localStorage.removeItem('chatwoot-tab-active');
      }, 100);
    } catch (error) {
      console.error('Error setting localStorage tab active event:', error);
    }
  }

  /**
   * Close BroadcastChannel connection
   * Should be called when app unmounts (optional)
   */
  destroy() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.isInitialized = false;
  }
}

// Export singleton instance
export default new SessionManager();
